<script>
import ContentBox from '@/components/ui/ContentBox';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import RequirePlan from '@/components/ui/RequirePlan';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'Support',
  components: {
    ContentBox,
    DashboardLayout,
    RequirePlan
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: this.$t('Support'),
      breadcrumbs: [{ text: this.$t('Support') }],
      cleanState: {},
      form: {
        type: 'General Support',
        details: '',
        isPriority:
          ['business', 'agency'].includes(this.$store.state.user.account.plan) && this.$store.state.user.isAdmin,
        url: []
      },
      invalid: [],
      isDisabled: false,
      isSaving: false,
      sites: []
    };
  },
  created() {
    // Set the intial clean state
    this.resetCleanState();
  },
  mounted() {
    this.$refs.type.focus();
  },
  beforeRouteLeave(to, from, next) {
    // Check for unsaved changes
    if (this.isDirty()) {
      this.$confirm(this.$t("Your changes haven't been saved. Are you sure you want to continue?"), {
        title: this.$t('Unsaved Changes'),
        confirmButtonText: this.$t('Continue'),
        cancelButtonText: this.$t('Cancel')
      }).then(() => next());
    } else {
      next();
    }
  },
  methods: {
    // Gets a list of sites for autocomplete
    getSites(search) {
      this.$http
        .get('/sites', {
          params: {
            search,
            limit: 20,
            sortBy: 'name'
          }
        })
        .then(res => (this.sites = res.data.sites))
        .catch(() => (this.sites = []));
    },

    // Returns the form's data
    getData() {
      return Object.assign({}, this.form);
    },

    // Sets the form's data
    setData(data) {
      Object.keys(this.form).forEach(key => {
        this.form[key] = typeof data[key] === 'undefined' ? this.form[key] : data[key];
      });
    },

    // Tells if the current state is different than the clean state
    isDirty() {
      return JSON.stringify(this.cleanState) !== JSON.stringify(this.form);
    },

    // Sets the clean state to the current state
    resetCleanState() {
      this.cleanState = Object.assign({}, this.form);
    },

    // Create the support request
    handleSave() {
      const data = this.getData();
      this.isSaving = true;
      this.$http
        .post('/support/request', {
          type: data.type,
          details: data.details,
          url: data.url,
          isPriority: data.isPriority
        })
        .then(() => {
          this.isDisabled = true;
          this.resetCleanState();
          this.$notify({
            type: 'success',
            title: this.title,
            message: this.$t("Your request has been received. We'll be in touch soon!")
          });
        })
        .catch(err => {
          const data = err.response.data;
          this.invalid = Array.isArray(data.invalid) ? data.invalid : [];
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isSaving = false));
    }
  }
};
</script>

<template>
  <dashboard-layout size="small">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Support form -->
    <el-form
      ref="supportForm"
      :disabled="isDisabled"
      class="support-form"
      label-position="top"
      @submit.native.prevent="handleSave"
    >
      <content-box>
        <fieldset>
          <!-- Type -->
          <el-form-item :label="$t('What do you need help with?')" :class="{ 'is-error': invalid.includes('type') }">
            <el-select ref="type" v-model="form.type" auto-complete="off">
              <el-option value="General Support">
                {{ $t('General Support') }}
              </el-option>
              <el-option value="Feature Request">
                {{ $t('Feature Request') }}
              </el-option>
              <el-option value="Bug Report">
                {{ $t('Bug Report') }}
              </el-option>
              <el-option value="Billing">
                {{ $t('Billing') }}
              </el-option>
            </el-select>
          </el-form-item>

          <!-- URL -->
          <el-form-item :label="$t('Which site does this involve?')" :class="{ 'is-error': invalid.includes('url') }">
            <el-select
              v-model="form.url"
              :class="{ 'is-error': invalid.includes('url') }"
              :debounce="300"
              :remote-method="getSites"
              :loading-text="$t('Loading...')"
              :multiple-limit="5"
              :no-data-text="$t('No Results')"
              :placeholder="$t('Start typing to add a site...')"
              auto-complete="off"
              multiple
              filterable
              remote
            >
              <el-option
                v-for="site in sites"
                :key="site.id"
                :label="site.name"
                :value="`${site.name} (${site.url})`"
              />
            </el-select>
          </el-form-item>

          <!-- Details -->
          <el-form-item
            :label="$t('Please provide details about the issue.')"
            :class="{ 'is-error': invalid.includes('details') }"
          >
            <el-input
              v-model="form.details"
              :class="{ 'is-error': invalid.includes('details') }"
              :rows="4"
              type="textarea"
              auto-complete="off"
            />
            <div class="text-form-description">
              {{
                $t(
                  'Provide as much detail as necessary. For example, how can the problem be replicated? Are you receiving an error message?'
                )
              }}
            </div>
          </el-form-item>

          <!-- Tip: English only -->
          <el-alert
            v-if="$store.state.user.isAdmin && $store.state.locale !== 'en'"
            type="info"
            title="Just a reminder!"
          >
            <div class="el-alert__description">
              At this time, we’re only able to read and respond to help requests in English.
            </div>
          </el-alert>

          <!-- Tip: Cancel account -->
          <el-alert
            v-if="
              $store.state.user.canManageBilling &&
              form.details.match(/(?=.*cancel|discontinue|stop)(?=.*(account|subscription|payments))/i)
            "
            type="info"
            title="Do you need to cancel your subscription?"
          >
            <div class="el-alert__description">
              You can do that on the
              <router-link :to="{ name: 'Billing' }"> billing page </router-link>.
            </div>
          </el-alert>

          <!-- Tip: Update payment info -->
          <el-alert
            v-if="
              $store.state.user.canManageBilling &&
              form.details.match(/(?=.*change|new|update)(?=.*(billing|credit card|payment))/i)
            "
            type="info"
            title="Are you trying to update your payment info?"
          >
            <div class="el-alert__description">
              You can do that on the
              <router-link :to="{ name: 'Billing' }"> billing page </router-link>.
            </div>
          </el-alert>

          <!-- Tip: Looking for invoices -->
          <el-alert
            v-if="$store.state.user.canManageBilling && form.details.match(/(?=.*invoice|receipt)/i)"
            type="info"
            title="Are you looking for past invoices?"
          >
            <div class="el-alert__description">
              You can find them on the
              <router-link :to="{ name: 'Billing' }"> billing page </router-link>.
            </div>
          </el-alert>

          <!-- Tip: PayPal -->
          <el-alert
            v-if="$store.state.user.canManageBilling && form.details.match(/(?=.*paypal)/i)"
            type="info"
            title="Are you asking about PayPal?"
          >
            <div class="el-alert__description">
              Unfortunately, we’re only able to accept major credit cards through Stripe at this time.
            </div>
          </el-alert>

          <!-- Tip: Phone support -->
          <el-alert
            v-if="$store.state.user.isAdmin && form.details.match(/(?=.*phone|call you|call me)/i)"
            type="info"
            title="Are you trying to call us?"
            description=""
          >
            <div class="el-alert__description">
              We don’t offer telephone support, but never fear! We are fantastic at email, and we’ll get back to you
              with a helpful response in no time.
            </div>
          </el-alert>

          <!-- Prioritize my request -->
          <el-form-item v-if="$store.state.user.isAdmin">
            <el-checkbox
              v-model="form.isPriority"
              :disabled="!['business', 'agency'].includes($store.state.user.account.plan)"
              style="margin-right: 0.5rem"
            >
              {{ $t('Prioritize My Request') }}
            </el-checkbox>
            <require-plan :plan="['business', 'agency']" />
            <div class="text-form-description">
              {{ $t('Get a helpful response in less than 24 hours, even on weekends and holidays.') }}
            </div>
          </el-form-item>
        </fieldset>
      </content-box>

      <div class="text-right">
        <!-- Submit -->
        <el-button :loading="isSaving" type="primary" native-type="submit">
          {{ $t('Submit Request') }}
        </el-button>
      </div>
    </el-form>
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.el-alert {
  margin-bottom: 1.5rem;
}
</style>
